<template>
  <section id="features" class="features-section" aria-labelledby="features-title">
    <div class="features-container">
      <div class="features-header" data-reveal="up">
        <span class="section-label">{{ $t('landing.features_label') }}</span>
        <h2 id="features-title" class="section-title">
          <template v-if="props.title">{{ props.title }}</template>
          <template v-else>{{ $t('landing.features_title_pre') }} <span class="gradient-text">{{ $t('landing.features_title_highlight') }}</span></template>
        </h2>
        <p class="section-subtitle">{{ props.subtitle || $t('landing.features_subtitle') }}</p>
      </div>

      <div class="features-grid">
        <div
          v-for="(feature, index) in features"
          :key="feature.key"
          class="feature-card"
          data-reveal="up"
          :data-reveal-delay="index + 1"
        >
          <div class="card-glow-line"></div>
          <div class="feature-icon" :class="feature.iconClass">
            <component :is="feature.icon" />
          </div>
          <h3 class="feature-title">{{ feature.customTitle || $t(`landing.feature_${feature.key}_title`) }}</h3>
          <p class="feature-desc">{{ feature.customDesc || $t(`landing.feature_${feature.key}_desc`) }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { h } from 'vue'

// SVG icon components (inline to avoid dependencies)
const IconDistribution = () => h('svg', { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 1.5, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
    h('circle', { cx: 12, cy: 12, r: 3 }),
    h('path', { d: 'M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83' })
])

const IconShoplinks = () => h('svg', { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 1.5, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
    h('path', { d: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71' }),
    h('path', { d: 'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71' })
])

const IconGates = () => h('svg', { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 1.5, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
    h('rect', { x: 3, y: 11, width: 18, height: 11, rx: 2, ry: 2 }),
    h('path', { d: 'M7 11V7a5 5 0 0 1 10 0v4' })
])

const IconMarketplace = () => h('svg', { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 1.5, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
    h('path', { d: 'M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z' }),
    h('line', { x1: 3, y1: 6, x2: 21, y2: 6 }),
    h('path', { d: 'M16 10a4 4 0 0 1-8 0' })
])

const IconAnalytics = () => h('svg', { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 1.5, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
    h('line', { x1: 18, y1: 20, x2: 18, y2: 10 }),
    h('line', { x1: 12, y1: 20, x2: 12, y2: 4 }),
    h('line', { x1: 6, y1: 20, x2: 6, y2: 14 })
])

const IconTeam = () => h('svg', { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 1.5, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
    h('path', { d: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' }),
    h('circle', { cx: 9, cy: 7, r: 4 }),
    h('path', { d: 'M23 21v-2a4 4 0 0 0-3-3.87' }),
    h('path', { d: 'M16 3.13a4 4 0 0 1 0 7.75' })
])

const props = defineProps({
  title: String,
  subtitle: String,
  items: { type: Array, default: null }
})

const iconMap = {
  distribution: IconDistribution,
  shoplinks: IconShoplinks,
  gates: IconGates,
  marketplace: IconMarketplace,
  analytics: IconAnalytics,
  teams: IconTeam,
  music: IconDistribution,
  rocket: IconShoplinks,
  shield: IconGates,
  star: IconAnalytics,
}

const defaultFeatures = [
    { key: 'distribution', icon: IconDistribution, iconClass: 'icon-cyan' },
    { key: 'shoplinks', icon: IconShoplinks, iconClass: 'icon-coral' },
    { key: 'gates', icon: IconGates, iconClass: 'icon-cyan' },
    { key: 'marketplace', icon: IconMarketplace, iconClass: 'icon-coral' },
    { key: 'analytics', icon: IconAnalytics, iconClass: 'icon-cyan' },
    { key: 'teams', icon: IconTeam, iconClass: 'icon-coral' },
]

const features = computed(() => {
  if (props.items && props.items.length > 0) {
    return props.items.map((item, i) => ({
      key: `custom-${i}`,
      icon: iconMap[item.icon] || IconDistribution,
      iconClass: i % 2 === 0 ? 'icon-cyan' : 'icon-coral',
      customTitle: item.title,
      customDesc: item.description,
    }))
  }
  return defaultFeatures
})
</script>

<style scoped>
/* Mobile-first. Ladder: 480 / 640 / 768 / 1024 / 1280 (variables.css). */
.features-section {
  position: relative;
  padding: var(--landing-section-pad) 0;
  background:
    radial-gradient(circle at 50% 50%, rgb(var(--primary-channels) / 0.03) 0%, transparent 70%),
    var(--bg-color);
}

.features-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-6);
}

.features-header {
  text-align: center;
  margin-bottom: clamp(40px, 5vw, 64px);
}

.section-label {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--primary);
  margin-bottom: var(--space-4);
}

.section-title {
  font-family: var(--font-heading);
  text-transform: uppercase;
  font-size: var(--landing-title-size);
  font-weight: 800;
  line-height: 1.1;
  color: #fff;
  margin-bottom: var(--space-4);
  text-wrap: balance;
}

.gradient-text {
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.section-subtitle {
  font-size: var(--landing-sub-size);
  color: var(--text-muted);
  max-width: 480px;
  margin: 0 auto;
}

/* Grid */
.features-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 640px) {
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }
}

@media (min-width: 1024px) {
  .features-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Card */
.feature-card {
  position: relative;
  background: var(--surface-color);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  padding: clamp(26px, 2.6vw + 18px, 36px) clamp(22px, 2.4vw + 16px, 32px);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
}

.feature-card:hover {
  border-color: rgb(var(--primary-channels) / 0.2);
  box-shadow: 0 0 40px rgb(var(--primary-channels) / 0.05);
  transform: translateY(-4px);
}

.card-glow-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(to right, transparent, var(--primary), transparent);
  opacity: 0.3;
}

.feature-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.icon-cyan {
  background: rgb(var(--primary-channels) / 0.1);
  color: var(--primary);
}

.icon-coral {
  background: rgb(var(--secondary-channels) / 0.1);
  color: var(--secondary);
}

.feature-title {
  font-family: var(--font-heading);
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 10px;
}

.feature-desc {
  font-size: 0.92rem;
  color: var(--text-muted);
  line-height: 1.6;
}

</style>
